import { computed, watch, onMounted, ref } from 'vue'
import { useSessionStore } from '@/store/pinia/SessionStore'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'

/**
 * Whitelabel Theme Management Composable
 *
 * Handles theme switching based on whitelabel identifier by applying
 * CSS classes to the body element. All themes are pre-loaded in CSS,
 * switching is instantaneous with no re-rendering.
 */

export const useWhitelabelTheme = () => {
  const sessionStore = useSessionStore()
  const isInitialized = ref(false)

  // Map whitelabel identifiers to theme class names
  const THEME_MAP = {
    'otivo': 'theme-otivo',
    'otivo-dark': 'theme-otivo-dark',
    'cfs': 'theme-cfs',
    'assure': 'theme-assure',
    'test': 'theme-test',
    'demo': 'demo-theme',
  } as const

  type ThemeIdentifier = keyof typeof THEME_MAP
  type ThemeClass = typeof THEME_MAP[ThemeIdentifier]

  /**
   * Get current theme identifier from whitelabel data
   */
  const currentThemeIdentifier = computed<ThemeIdentifier>(() => {
    // Check if demo mode is enabled via feature flag
    const isDemoMode = useLDFlag(FeatureList.ENABLE_DEMO_MODE, false).value
    if (isDemoMode) {
      return 'demo'
    }

    const identifier = sessionStore.getWhitelabelData?.identifier as ThemeIdentifier
    return identifier && identifier in THEME_MAP ? identifier : 'otivo'
  })

  /**
   * Get current theme CSS class name
   */
  const currentThemeClass = computed<ThemeClass>(() => {
    return THEME_MAP[currentThemeIdentifier.value]
  })

  /**
   * Apply a theme by updating body class
   */
  const applyTheme = (themeClass: ThemeClass) => {
    if (typeof document === 'undefined') return

    // Add loading class to prevent transition flash
    document.body.classList.add('theme-loading')

    // Remove any existing theme classes
    Object.values(THEME_MAP).forEach(className => {
      document.body.classList.remove(className)
    })

    // Apply new theme class
    document.body.classList.add(themeClass)

    // Remove loading class after a short delay to allow CSS to settle
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.body.classList.remove('theme-loading')
      })
    })

    console.log(`Applied theme: ${themeClass}`)
  }

  /**
   * Manually switch to a specific theme (useful for testing/admin)
   */
  const switchToTheme = (identifier: ThemeIdentifier) => {
    applyTheme(THEME_MAP[identifier])
  }

  /**
   * Get list of available themes
   */
  const availableThemes = computed(() => {
    return Object.entries(THEME_MAP).map(([identifier, className]) => ({
      identifier: identifier as ThemeIdentifier,
      className,
      displayName: identifier.charAt(0).toUpperCase() + identifier.slice(1)
    }))
  })

  /**
   * Initialize theme system and watch for changes
   */
  const initializeTheme = () => {
    if (isInitialized.value) return

    // Apply initial theme
    applyTheme(currentThemeClass.value)

    // Watch for whitelabel data changes
    watch(
      currentThemeClass,
      (newThemeClass) => {
        applyTheme(newThemeClass)
      },
      { immediate: false } // Don't fire immediately since we already applied initial theme
    )

    isInitialized.value = true
  }

  // Auto-initialize when mounted
  onMounted(() => {
    initializeTheme()
  })

  return {
    // State
    currentThemeIdentifier,
    currentThemeClass,
    availableThemes,
    isInitialized,

    // Actions
    applyTheme,
    switchToTheme,
    initializeTheme,
  }
}
